import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useConfirm } from "../lib/confirm";
import { CompassLogo } from "../components/CompassLogo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import SuperAdminCategoryTabs from "../components/SuperAdminCategoryTabs";
import SuperAdminObedienceStats from "../components/SuperAdminObedienceStats";
import { Toaster } from "sonner";
import {
  Plus,
  Trash2,
  X,
  LogOut,
  Building2,
  Users,
  UserCog,
  Copy,
  KeyRound,
  ShieldCheck,
  Megaphone,
  IdCard,
  Pencil,
  Smartphone,
} from "lucide-react";

const emptyForm = {
  lodge_name: "",
  city: "",
  cityCustom: "",   // free-text fallback when picker = __other__
  country: "RO",
  obedience_abbreviation: "",
  obedience_type: "regulara",
  admin_username: "",
  admin_name: "",
  admin_phone_number: "",
  // Access window
  accessMode: "trial7",
  trialDays: 7,
  customDateTime: "",
  // Tenant quotas — child-lodges as a 50-bucket range, app-user credentials
  // as a 3-tier hard cap (member directory remains unlimited).
  max_child_lodges: 0,
  max_app_users: 0,
};

const emptyPwdForm = { current_password: "", new_password: "", confirm_password: "" };

const emptyAccessForm = { accessMode: "trial7", trialDays: 7, customDateTime: "" };

const emptyMessageForm = { lodge_ids: [], all: true, title: "", body: "" };

/** Public production URL (used as a fallback if the SuperAdmin opens this
 * page from a non-public host like the preview environment). The actual
 * displayed URL is computed at render time from `window.location.origin`,
 * so credentials created on preview send the preview link (where the data
 * actually lives) and credentials created on the production lodge subdomain
 * send the production link. This prevents the "preview vs production DB"
 * mismatch that was breaking lodge admin login. */
const PUBLIC_LODGE_LOGIN_URL = "https://lodge.squareandcompass.store/login";

function getLodgeLoginUrl() {
  if (typeof window === "undefined") return PUBLIC_LODGE_LOGIN_URL;
  const host = window.location.hostname || "";
  // On preview / dev / apex without a configured lodge.* subdomain, fall back
  // to the current origin so the URL we show matches the data we just wrote.
  if (host === "lodge.squareandcompass.store") {
    return PUBLIC_LODGE_LOGIN_URL;
  }
  return `${window.location.origin}/login`;
}

/** Build a WhatsApp/email-friendly credentials block for the COPY ALL button. */
function buildCredentialsBundle({ lodgeName, username, password, url, lang }) {
  if (lang === "en") {
    return (
      `Square & Compass — Lodge access\n` +
      `Lodge: ${lodgeName}\n` +
      `Username: ${username}\n` +
      `Password: ${password}\n` +
      `Login: ${url}`
    );
  }
  return (
    `Square & Compass — Acces lojă\n` +
    `Lojă: ${lodgeName}\n` +
    `Utilizator: ${username}\n` +
    `Parolă: ${password}\n` +
    `Autentificare: ${url}`
  );
}

function buildAccessPayload(f) {
  if (f.accessMode === "unlimited") return { unlimited: true };
  if (f.accessMode === "custom" && f.customDateTime) {
    return { access_expires_at: new Date(f.customDateTime).toISOString() };
  }
  if (f.accessMode === "trialN") {
    return { trial_days: Math.max(1, parseInt(f.trialDays || 7, 10)) };
  }
  // default trial7
  return { trial_days: 7 };
}

function accessStatus(lodge, t, locale) {
  const exp = lodge.access_expires_at;
  if (!exp) {
    return { label: t("superadmin.accessUnlimited"), color: "#3e6a3c", bg: "#e8f0e5", border: "#b9cfb4" };
  }
  const expDt = new Date(exp);
  const now = new Date();
  if (expDt <= now) {
    return { label: t("superadmin.accessExpired"), color: "#7c2a2a", bg: "#f6e0e0", border: "#d9b9b9" };
  }
  return {
    label:
      t("superadmin.accessTrial") +
      " · " +
      expDt.toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" }) +
      ", " +
      expDt.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }),
    color: "#9a7530",
    bg: "#fdf4e0",
    border: "#c9a84c",
  };
}

export default function SuperAdminPage() {
  const { t, i18n } = useTranslation();
  const locale =t("superAdmin.en_gb");
  const { user, logout } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();

  const [lodges, setLodges] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [resetForm, setResetForm] = useState({ username: "", password: "", name: "", role: "admin" });
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const [pwdForm, setPwdForm] = useState(emptyPwdForm);
  const [pwdSaving, setPwdSaving] = useState(false);

  // Country + cities reference data for the new-lodge dropdowns.
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);

  // MFA picker — opens a dialog with every admin/officer in a lodge so
  // the SA can reset MFA for one specific account instead of all at once.
  const [mfaPickerFor, setMfaPickerFor] = useState(null);
  const [mfaPickerUsers, setMfaPickerUsers] = useState([]);
  const [mfaPickerLoading, setMfaPickerLoading] = useState(false);

  // Auto-generated password surfaced after RESET so the SA can copy it once.
  const [resetGeneratedPwd, setResetGeneratedPwd] = useState(null);

  // Access management state
  const [accessFor, setAccessFor] = useState(null);
  const [accessForm, setAccessForm] = useState(emptyAccessForm);
  const [accessSaving, setAccessSaving] = useState(false);

  // Edit lodge state
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  // Send message state
  const [showMessage, setShowMessage] = useState(false);
  const [messageForm, setMessageForm] = useState(emptyMessageForm);
  const [messageSending, setMessageSending] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/superadmin/lodges");
      setLodges(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Load country list once (for the new-lodge dropdown).
  useEffect(() => {
    api.get("/reference/countries").then(({ data }) => setCountries(data || [])).catch(() => {});
  }, []);

  // Whenever the user picks a country, fetch the matching curated city list.
  useEffect(() => {
    if (!form.country) {
      setCities([]);
      return;
    }
    api
      .get(`/reference/cities?country=${encodeURIComponent(form.country)}`)
      .then(({ data }) => setCities(data || []))
      .catch(() => setCities([]));
  }, [form.country]);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const create = async (e) => {
    e.preventDefault();

    // Username MUST be a valid email — front-end soft check (server enforces too).
    const username = (form.admin_username || "").trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(username)) {
      toast.error(t("superAdmin.username_must_be_a_valid"),
      );
      return;
    }

    // Resolve city: dropdown value OR free text fallback when "__other__".
    const resolvedCity = form.city === "__other__" ? (form.cityCustom || "").trim() : form.city;

    // Resolve country code → human name (server stores name string).
    const countryEntry = countries.find((c) => c.code === form.country);
    const countryName = countryEntry ? (i18n.language !== "ro" ? countryEntry.name_en : countryEntry.name_ro) : form.country;

    setSaving(true);
    setError("");
    try {
      const payload = {
        lodge_name: form.lodge_name,
        city: resolvedCity,
        country: countryName,
        obedience_abbreviation: form.obedience_abbreviation,
        obedience_type: form.obedience_type || "regulara",
        admin_username: username,
        // Backend auto-generates a strong temporary password when omitted.
        admin_name: form.admin_name,
        admin_phone_number: form.admin_phone_number || null,
        max_child_lodges: form.max_child_lodges ? parseInt(form.max_child_lodges, 10) : null,
        max_app_users: form.max_app_users ? parseInt(form.max_app_users, 10) : null,
      };
      const access = buildAccessPayload(form);
      if ("trial_days" in access) payload.trial_days = access.trial_days;
      if ("access_expires_at" in access) payload.access_expires_at = access.access_expires_at;
      // unlimited → omit both, backend treats as null
      const { data } = await api.post("/superadmin/lodges", payload);
      setCreated({ lodge: data.lodge, admin: data.admin, plaintext_password: data.plaintext_password });
      setForm(emptyForm);
      setShowForm(false);
      toast.success(t("superadmin.createdSuccess"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (l) => {
    const ok = await confirm({
      title: t("common.delete"),
      message: t("superadmin.deleteConfirm", { name: l.name }),
    });
    if (!ok) return;
    try {
      await api.delete(`/superadmin/lodges/${l.id}`);
      toast.success(t("recognized.deletedSuccess"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resetMfa = async (l) => {
    // Per user spec: instead of nuking every admin in one click, open a
    // picker that lists every account in the lodge and lets the SA reset
    // each one individually.
    setMfaPickerFor(l);
    setMfaPickerLoading(true);
    setMfaPickerUsers([]);
    try {
      const { data } = await api.get(`/superadmin/lodges/${l.id}/admins`);
      setMfaPickerUsers(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
      setMfaPickerFor(null);
    } finally {
      setMfaPickerLoading(false);
    }
  };

  const resetSingleUserMfa = async (u) => {
    if (!window.confirm(
      i18n.language !== "ro"
        ? `Wipe Google Authenticator for ${u.username}?`
        : `Resetez Google Authenticator pentru ${u.username}?`,
    )) return;
    try {
      await api.post(`/superadmin/users/${u.id}/reset-mfa`);
      toast.success(
        i18n.language !== "ro"
          ? `MFA reset for ${u.username}.`
          : `MFA resetat pentru ${u.username}.`,
      );
      // refresh the picker list so mfa_enabled flips off
      if (mfaPickerFor) {
        try {
          const { data } = await api.get(`/superadmin/lodges/${mfaPickerFor.id}/admins`);
          setMfaPickerUsers(data || []);
        } catch { /* noop */ }
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openResetFor = async (l) => {
    setResetFor(l);
    setResetForm({ username: "", password: "", name: "", role: "admin" });
    // Pre-populate from the lodge's primary admin so the SA can edit
    // instead of retyping. Password stays empty: leaving it blank tells
    // the backend to auto-generate a strong temp password on save.
    try {
      const { data } = await api.get(`/superadmin/lodges/${l.id}/primary-admin`);
      setResetForm((s) => ({
        ...s,
        username: data.username || "",
        name: data.name || "",
      }));
    } catch { /* fall back to empty modal */ }
  };

  const resetAdmin = async (e) => {
    e.preventDefault();
    setResetGeneratedPwd(null);
    try {
      const payload = {
        username: resetForm.username,
        name: resetForm.name,
        role: "admin",
      };
      if ((resetForm.password || "").trim()) {
        payload.password = resetForm.password.trim();
      }
      const { data } = await api.post(`/superadmin/lodges/${resetFor.id}/reset-admin`, payload);
      toast.success(
        i18n.language !== "ro"
          ? `Admin reset for ${resetFor.name}.`
          : `Admin resetat pentru ${resetFor.name}.`,
      );
      // Show the auto-generated password (if backend made one) so the SA
      // can copy and share it once. The dialog stays open until the SA
      // closes it manually.
      if (data.plaintext_password) {
        setResetGeneratedPwd({ password: data.plaintext_password, username: data.username });
      } else {
        setResetFor(null);
        setResetForm({ username: "", password: "", name: "", role: "admin" });
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openAccess = (l) => {
    // Pre-fill the form from current state
    if (!l.access_expires_at) {
      setAccessForm({ accessMode: "unlimited", trialDays: 7, customDateTime: "" });
    } else {
      const dt = new Date(l.access_expires_at);
      const local = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      setAccessForm({ accessMode: "custom", trialDays: 7, customDateTime: local });
    }
    setAccessFor(l);
  };

  const openEditLodge = (l) => {
    setEditForm({
      name: l.name || "",
      number: l.number || "",
      city: l.city || "",
      country: l.country || "",
      obedience: l.obedience || "",
      obedience_abbreviation: l.obedience_abbreviation || "",
      obedience_type: l.obedience_type || "regulara",
      motto: l.motto || "",
      meeting: l.meeting || "",
      logo_url: l.logo_url || "",
      admin_username: "",
      admin_password: "",
    });
    setEditFor(l);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      const payload = { ...editForm };
      if (!payload.admin_username) delete payload.admin_username;
      if (!payload.admin_password) delete payload.admin_password;
      await api.patch(`/superadmin/lodges/${editFor.id}/edit`, payload);
      toast.success(t("common.save"));
      setEditFor(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEditSaving(false);
    }
  };

  const submitAccess = async (e) => {
    e.preventDefault();
    setAccessSaving(true);
    try {
      const payload = buildAccessPayload(accessForm);
      await api.patch(`/superadmin/lodges/${accessFor.id}/access`, payload);
      toast.success(t("superadmin.accessSaved"));
      setAccessFor(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAccessSaving(false);
    }
  };

  const submitMessage = async (e) => {
    e.preventDefault();
    setMessageSending(true);
    try {
      const payload = {
        lodge_ids: messageForm.all ? [] : messageForm.lodge_ids,
        title: messageForm.title,
        body: messageForm.body,
      };
      const { data } = await api.post("/superadmin/announcements", payload);
      toast.success(t("superadmin.messageSent", { count: data.delivered_to }));
      setShowMessage(false);
      setMessageForm(emptyMessageForm);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setMessageSending(false);
    }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} ${t("superAdmin.copied")}.`);
    } catch {
      /* ignore */
    }
  };

  const submitPwdChange = async (e) => {
    e.preventDefault();
    if (pwdForm.new_password !== pwdForm.confirm_password) {
      toast.error(t("superadmin.passwordMismatch"));
      return;
    }
    setPwdSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwdForm.current_password,
        new_password: pwdForm.new_password,
      });
      toast.success(t("superadmin.passwordChanged"));
      setShowPwd(false);
      setPwdForm(emptyPwdForm);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPwdSaving(false);
    }
  };

  const toggleRecipient = (lid) => {
    setMessageForm((f) => ({
      ...f,
      lodge_ids: f.lodge_ids.includes(lid)
        ? f.lodge_ids.filter((x) => x !== lid)
        : [...f.lodge_ids, lid],
    }));
  };

  const messageRecipientLabel = useMemo(() => {
    if (messageForm.all) return t("superadmin.messageAllLodges") + ` (${lodges.length})`;
    return `${messageForm.lodge_ids.length} / ${lodges.length}`;
  }, [messageForm, lodges, t]);

  return (
    <div style={{ minHeight: "100vh", background: "var(--cream-100)" }} data-testid="superadmin-page">
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#0d1b2a",
            color: "#e8c97a",
            border: "1px solid #c9a84c",
            fontFamily: "'Crimson Text', serif",
            borderRadius: 0,
          },
        }}
      />

      <div className="topbar">
        <div className="topbar-lodge">
          <CompassLogo size={26} color="#e8c97a" accent="#e8c97a" />
          <div>
            <div className="topbar-lodge-name">SUPER ADMIN PANEL</div>
            <div className="topbar-lodge-city">{user?.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <LanguageSwitcher />
          <button
            onClick={() => setShowMessage(true)}
            className="topbar-logout"
            data-testid="sa-send-message-btn"
            style={{ background: "transparent", color: "#e8c97a", border: "1px solid rgba(201,168,76,0.5)" }}
          >
            <Megaphone size={12} style={{ display: "inline", marginRight: "0.3rem" }} />{" "}
            {t("superadmin.sendMessage")}
          </button>
          <button
            onClick={() => navigate("/superadmin/passport-numbers")}
            className="topbar-logout"
            data-testid="sa-passport-numbers-btn"
            style={{ background: "transparent", color: "#e8c97a", border: "1px solid rgba(201,168,76,0.5)" }}
          >
            <IdCard size={12} style={{ display: "inline", marginRight: "0.3rem" }} />{" "}
            {t("passports.title")}
          </button>
          <button
            onClick={() => setShowPwd(true)}
            className="topbar-logout"
            data-testid="sa-change-password"
            style={{ background: "transparent", color: "#e8c97a", border: "1px solid rgba(201,168,76,0.5)" }}
          >
            <KeyRound size={12} style={{ display: "inline", marginRight: "0.3rem" }} />{" "}
            {t("superadmin.changePassword")}
          </button>
          <button onClick={handleLogout} className="topbar-logout" data-testid="sa-logout">
            <LogOut size={12} style={{ display: "inline", marginRight: "0.3rem" }} /> {t("nav.logout")}
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        {/* Alert if SuperAdmin is being used on a non-public host (e.g. preview).
            Lodge admins created here will only be able to log in on this same
            host — NOT on the production lodge.* subdomain — because the
            databases are independent. */}
        {typeof window !== "undefined" &&
          window.location.hostname !== "lodge.squareandcompass.store" && (
            <div
              data-testid="non-prod-banner"
              style={{
                marginBottom: "1.25rem",
                padding: "0.85rem 1rem",
                background: "#fdf4e0",
                border: "1px solid #c9a84c",
                color: "#7a4f1c",
                fontFamily: "Crimson Text, serif",
                fontSize: "0.95rem",
                borderRadius: 4,
              }}
            >
              <strong style={{ fontFamily: "Cinzel, serif", fontSize: "0.78rem", letterSpacing: "0.12em" }}>
                {t("superAdmin.attention")}
              </strong>
              <span style={{ marginLeft: 8 }}>
                {i18n.language !== "ro"
                  ? `You are logged in on "${window.location.host}". Lodges created here are stored in this environment's database and lodge admins must log in on the SAME URL. To send credentials that work on https://lodge.squareandcompass.store/login, open SuperAdmin there: https://lodge.squareandcompass.store/superadmin`
                  : `Ești autentificat pe "${window.location.host}". Lojile create aici sunt salvate în baza de date a acestui mediu, iar adminii lor se pot autentifica DOAR pe același URL. Pentru ca link-ul https://lodge.squareandcompass.store/login să funcționeze pentru clienți, accesează SuperAdmin direct acolo: https://lodge.squareandcompass.store/superadmin`}
              </span>
            </div>
          )}

        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="page-title">{t("superadmin.title")}</h1>
            <p className="page-subtitle">{t("superadmin.subtitle")}</p>
          </div>
          <button
            className="btn-primary-gold flex items-center gap-2"
            onClick={() => setShowForm(true)}
            data-testid="create-lodge-btn"
          >
            <Plus size={14} /> {t("superadmin.newLodge")}
          </button>
        </div>

        <div className="gold-divider my-8" />

        <SuperAdminCategoryTabs active="obediences" />

        <SuperAdminObedienceStats />

        {error && <div className="error-banner mb-4">{error}</div>}

        {created && (
          <div
            className="upload-box mb-5"
            style={{ borderColor: "#c9a84c", background: "#fdf4e0" }}
            data-testid="created-creds-box"
          >
            <div className="upload-box-head" style={{ color: "#9a7530" }}>
              {t("superAdmin.new_lodge_credentials_save_them")}
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="kv-label">{t("recognized.title").slice(0, 5)}:</span>{" "}
                <strong>{created.lodge.name}</strong>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="kv-label">{t("login.username")}:</span>
                <code style={{ background: "#fff", padding: "0.15rem 0.5rem", border: "1px solid #c9a84c" }}>
                  {created.admin.username}
                </code>
                <button
                  className="btn-outline-gold flex items-center gap-1"
                  onClick={() => copyText(created.admin.username, t("login.username"))}
                  data-testid="copy-username-btn"
                >
                  <Copy size={10} /> COPY
                </button>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="kv-label">{t("login.password")}:</span>
                <code style={{ background: "#fff", padding: "0.15rem 0.5rem", border: "1px solid #c9a84c" }}>
                  {created.plaintext_password}
                </code>
                <button
                  className="btn-outline-gold flex items-center gap-1"
                  onClick={() => copyText(created.plaintext_password, t("login.password"))}
                  data-testid="copy-password-btn"
                >
                  <Copy size={10} /> COPY
                </button>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="kv-label">URL:</span>
                <code
                  style={{ background: "#fff", padding: "0.15rem 0.5rem", border: "1px solid #c9a84c" }}
                  data-testid="created-url-value"
                >
                  {getLodgeLoginUrl()}
                </code>
                <button
                  className="btn-outline-gold flex items-center gap-1"
                  onClick={() => copyText(getLodgeLoginUrl(), "URL")}
                  data-testid="copy-url-btn"
                >
                  <Copy size={10} /> COPY
                </button>
              </div>
              <div className="flex items-center gap-2 flex-wrap mt-3">
                <button
                  className="btn-primary-gold flex items-center gap-1"
                  onClick={() =>
                    copyText(
                      buildCredentialsBundle({
                        lodgeName: created.lodge.name,
                        username: created.admin.username,
                        password: created.plaintext_password,
                        url: getLodgeLoginUrl(),
                        lang: i18n.language,
                      }),t("superAdmin.all_credentials"),
                    )
                  }
                  data-testid="copy-all-btn"
                >
                  <Copy size={12} />{" "}
                  {t("superAdmin.copy_all_whatsapp_email")}
                </button>
              </div>
              <button className="btn-outline-gold mt-2" onClick={() => setCreated(null)}>
                {t("common.close")}
              </button>
            </div>
          </div>
        )}

        <table className="lodge-table" data-testid="tenant-lodges-table">
          <thead>
            <tr>
              <th>{t("superadmin.lodgeName")}</th>
              <th>{t("superadmin.city")}</th>
              <th>{t("superadmin.obedience")}</th>
              <th>
                <Users size={13} style={{ display: "inline" }} /> Admins
              </th>
              <th>
                <UserCog size={13} style={{ display: "inline" }} /> {t("nav.members")}
              </th>
              <th>{t("superadmin.access")}</th>
              <th>{t("users.createdAt")}</th>
              <th style={{ textAlign: "right" }}>{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {lodges.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", fontStyle: "italic", color: "#858481", padding: "2rem" }}>
                  {t("recognized.noResults")}
                </td>
              </tr>
            ) : (
              lodges.map((l) => {
                const st = accessStatus(l, t, locale);
                return (
                  <tr key={l.id}>
                    <td>
                      <div className="flex items-center gap-2">
                        <Building2 size={16} color="#c9a84c" />
                        <div>
                          <div style={{ fontFamily: "Cinzel, serif", color: "#0d1b2a", fontSize: "0.95rem" }}>
                            {l.name}
                          </div>
                          {l.number && <div className="kv-label" style={{ fontSize: "0.62rem" }}>{l.number}</div>}
                        </div>
                      </div>
                    </td>
                    <td>{[l.city, l.country].filter(Boolean).join(", ") || "—"}</td>
                    <td>{l.obedience || "—"}</td>
                    <td>
                      <span className="badge-grade">{l.admin_count}</span>
                    </td>
                    <td>
                      <span className="badge-grade">{l.member_count}</span>
                    </td>
                    <td>
                      <span
                        className="badge-status"
                        data-testid={`access-status-${l.id}`}
                        style={{
                          color: st.color,
                          background: st.bg,
                          border: `1px solid ${st.border}`,
                          fontSize: "0.62rem",
                        }}
                      >
                        {st.label}
                      </span>
                    </td>
                    <td>{new Date(l.created_at).toLocaleDateString(locale)}</td>
                    <td style={{ textAlign: "right" }}>
                      <div className="flex justify-end gap-2 flex-wrap">
                        <button
                          className="btn-outline-gold flex items-center gap-1"
                          onClick={() => openEditLodge(l)}
                          data-testid={`edit-lodge-${l.id}`}
                        >
                          <Pencil size={12} /> {t("common.edit", "EDIT")}
                        </button>
                        <button
                          className="btn-outline-gold flex items-center gap-1"
                          onClick={() => openAccess(l)}
                          data-testid={`manage-access-${l.id}`}
                        >
                          <ShieldCheck size={12} /> {t("superadmin.manageAccess")}
                        </button>
                        <button
                          className="btn-outline-gold flex items-center gap-1"
                          onClick={() => openResetFor(l)}
                          data-testid={`reset-admin-${l.id}`}
                        >
                          <UserCog size={12} /> RESET
                        </button>
                        <button
                          className="btn-outline-gold flex items-center gap-1"
                          onClick={() => resetMfa(l)}
                          data-testid={`reset-mfa-${l.id}`}
                          title={t("superAdmin.reset_mfa_force_re_pair")}
                        >
                          <Smartphone size={12} /> MFA
                        </button>
                        <button
                          className="btn-danger flex items-center gap-1"
                          onClick={() => remove(l)}
                          data-testid={`delete-tenant-${l.id}`}
                        >
                          <Trash2 size={12} /> {t("common.delete")}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Create lodge modal */}
      {showForm && (
        <div className="dialog-overlay" onClick={() => setShowForm(false)}>
          <form
            className="dialog-panel"
            onClick={(e) => e.stopPropagation()}
            onSubmit={create}
            data-testid="create-lodge-form"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title" style={{ margin: 0 }}>
                {t("superadmin.newLodge")}
              </h2>
              <button type="button" onClick={() => setShowForm(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="section-heading" style={{ marginBottom: "0.6rem" }}>
              {t("lodgeInfo.generalInfo")}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <div className="md:col-span-2">
                <label className="label-lodge">
                  {t("superAdmin.obedience_name")}
                </label>
                <input
                  className="input-lodge"
                  required
                  value={form.lodge_name}
                  onChange={(e) => setForm({ ...form, lodge_name: e.target.value })}
                  data-testid="input-lodge-name"
                  placeholder={t("superAdmin.e_g_grand_lodge_of")}
                />
              </div>
              <div className="md:col-span-2">
                <label className="label-lodge">{t("superadmin.obedienceAbbrev")}</label>
                <input
                  className="input-lodge"
                  value={form.obedience_abbreviation}
                  onChange={(e) => setForm({ ...form, obedience_abbreviation: e.target.value.toUpperCase() })}
                  placeholder="MLNR, MLTAV..."
                  maxLength={12}
                  data-testid="input-obedience-abbrev"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label-lodge">{t("superadmin.obedienceType", "Tip obediență")}</label>
                <select
                  className="input-lodge"
                  value={form.obedience_type}
                  onChange={(e) => setForm({ ...form, obedience_type: e.target.value })}
                  data-testid="input-obedience-type"
                >
                  <option value="regulara">{t("superadmin.obedienceTypeRegular", "Regulară")}</option>
                  <option value="mixta">{t("superadmin.obedienceTypeMixed", "Mixtă")}</option>
                  <option value="feminina">{t("superadmin.obedienceTypeFeminine", "Feminină")}</option>
                </select>
                <p className="kv-label" style={{ fontSize: "0.66rem", marginTop: 4 }}>
                  {t("superadmin.obedienceTypeHint", "Lojile și membrii se văd între obediențe doar dacă tipul este identic.")}
                </p>
              </div>
              <div>
                <label className="label-lodge">{t("superadmin.country")} *</label>
                <select
                  className="input-lodge"
                  required
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value, city: "" })}
                  data-testid="input-country"
                >
                  <option value="">{t("superAdmin.pick_country")}</option>
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>
                      {i18n.language !== "ro" ? c.name_en : c.name_ro}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-lodge">{t("superadmin.city")} *</label>
                {cities.length > 0 ? (
                  <>
                    <select
                      className="input-lodge"
                      required
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value, cityCustom: "" })}
                      data-testid="input-city"
                    >
                      <option value="">{t("superAdmin.pick_city")}</option>
                      {cities.map((cn) => (
                        <option key={cn} value={cn}>{cn}</option>
                      ))}
                      <option value="__other__">{t("superAdmin.other_type_below")}</option>
                    </select>
                    {form.city === "__other__" && (
                      <input
                        className="input-lodge mt-2"
                        required
                        placeholder={t("superAdmin.type_city_name")}
                        value={form.cityCustom}
                        onChange={(e) => setForm({ ...form, cityCustom: e.target.value })}
                        data-testid="input-city-custom"
                      />
                    )}
                  </>
                ) : (
                  <input
                    className="input-lodge"
                    required
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder={t("superAdmin.type_city_name")}
                    data-testid="input-city"
                  />
                )}
              </div>
            </div>

            <div className="section-heading" style={{ marginBottom: "0.6rem" }}>
              {t("superAdmin.initial_admin")}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <div className="md:col-span-2">
                <label className="label-lodge">{t("superadmin.adminName")} *</label>
                <input
                  className="input-lodge"
                  required
                  value={form.admin_name}
                  onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                  data-testid="input-admin-name"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label-lodge">
                  {t("superAdmin.admin_email_username")}
                </label>
                <input
                  type="email"
                  className="input-lodge"
                  required
                  value={form.admin_username}
                  onChange={(e) => setForm({ ...form, admin_username: e.target.value })}
                  data-testid="input-admin-username"
                  placeholder="admin@loja.ro"
                />
                <div className="kv-label" style={{ fontSize: "0.6rem", marginTop: 4 }}>
                  {t("superAdmin.a_strong_temporary_password_is")}
                </div>
              </div>

              <div>
                <label className="label-lodge">
                  {t("superAdmin.admin_phone_recovery")}
                </label>
                <input
                  type="tel"
                  className="input-lodge"
                  value={form.admin_phone_number}
                  onChange={(e) => setForm({ ...form, admin_phone_number: e.target.value })}
                  data-testid="input-admin-phone"
                  placeholder="+40712345678"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="label-lodge">
                  {t("superAdmin.max_child_lodges")}
                </label>
                <select
                  className="input-lodge"
                  value={form.max_child_lodges}
                  onChange={(e) => setForm({ ...form, max_child_lodges: e.target.value })}
                  data-testid="input-max-child-lodges"
                >
                  <option value="0">{t("superAdmin.unlimited")}</option>
                  {Array.from({ length: 50 }, (_, idx) => {
                    const lo = idx * 10 + 1;
                    const hi = (idx + 1) * 10;
                    return (
                      <option key={hi} value={hi}>{`${lo}-${hi}`}</option>
                    );
                  })}
                </select>
                <div className="kv-label" style={{ fontSize: "0.6rem", marginTop: 4 }}>
                  {t("superAdmin.hard_cap_on_how_many")}
                </div>
              </div>
              <div>
                <label className="label-lodge">
                  {t("superAdmin.officers_with_app_access")}
                </label>
                <select
                  className="input-lodge"
                  value={form.max_app_users}
                  onChange={(e) => setForm({ ...form, max_app_users: e.target.value })}
                  data-testid="input-max-app-users"
                >
                  <option value="0">{t("superAdmin.unlimited")}</option>
                  <option value="3">3</option>
                  <option value="7">7</option>
                  <option value="10">10</option>
                </select>
                <div className="kv-label" style={{ fontSize: "0.6rem", marginTop: 4 }}>
                  {t("superAdmin.number_of_officers_that_may")}
                </div>
              </div>
            </div>

            <div className="section-heading" style={{ marginBottom: "0.6rem" }}>
              {t("superadmin.accessSection")}
            </div>
            <p className="kv-label" style={{ fontSize: "0.66rem", marginBottom: "0.6rem" }}>
              {t("superadmin.accessHelp")}
            </p>
            <AccessFieldset value={form} onChange={(p) => setForm({ ...form, ...p })} idPrefix="create" />

            <div className="flex justify-end gap-3 mt-6">
              <button type="button" className="btn-outline-gold" onClick={() => setShowForm(false)}>
                {t("common.cancel")}
              </button>
              <button type="submit" className="btn-primary-gold" disabled={saving} data-testid="save-lodge-btn">
                {saving ? "..." : t("common.create")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit lodge modal */}
      {editFor && (
        <div className="dialog-overlay" onClick={() => setEditFor(null)}>
          <form
            className="dialog-panel"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitEdit}
            style={{ maxWidth: 560 }}
            data-testid="edit-lodge-form"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title" style={{ margin: 0 }}>
                {t("common.edit")} · {editFor.name}
              </h2>
              <button type="button" onClick={() => setEditFor(null)}><X size={20} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="label-lodge">{t("superadmin.lodgeName")}</label>
                <input className="input-lodge" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </div>
              <div>
                <label className="label-lodge">{t("superadmin.lodgeNumber")}</label>
                <input className="input-lodge" value={editForm.number} onChange={(e) => setEditForm({ ...editForm, number: e.target.value })} />
              </div>
              <div>
                <label className="label-lodge">{t("superadmin.obedience")}</label>
                <input className="input-lodge" value={editForm.obedience} onChange={(e) => setEditForm({ ...editForm, obedience: e.target.value })} />
              </div>
              <div>
                <label className="label-lodge">{t("superadmin.obedienceAbbrev")}</label>
                <input
                  className="input-lodge"
                  value={editForm.obedience_abbreviation}
                  onChange={(e) => setEditForm({ ...editForm, obedience_abbreviation: e.target.value.toUpperCase() })}
                  placeholder="MLNR, MLTAV..."
                  maxLength={12}
                  data-testid="edit-obedience-abbrev"
                />
              </div>
              <div>
                <label className="label-lodge">{t("superadmin.obedienceType", "Tip obediență")}</label>
                <select
                  className="input-lodge"
                  value={editForm.obedience_type}
                  onChange={(e) => setEditForm({ ...editForm, obedience_type: e.target.value })}
                  data-testid="edit-obedience-type"
                >
                  <option value="regulara">{t("superadmin.obedienceTypeRegular", "Regulară")}</option>
                  <option value="mixta">{t("superadmin.obedienceTypeMixed", "Mixtă")}</option>
                  <option value="feminina">{t("superadmin.obedienceTypeFeminine", "Feminină")}</option>
                </select>
              </div>
              <div>
                <label className="label-lodge">{t("superadmin.city")}</label>
                <input className="input-lodge" value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} />
              </div>
              <div>
                <label className="label-lodge">{t("superadmin.country")}</label>
                <input className="input-lodge" value={editForm.country} onChange={(e) => setEditForm({ ...editForm, country: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="label-lodge">{t("lodgeInfo.motto", "Moto")}</label>
                <input className="input-lodge" value={editForm.motto} onChange={(e) => setEditForm({ ...editForm, motto: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="label-lodge">{t("lodgeInfo.meeting", "Întâlniri")}</label>
                <input className="input-lodge" value={editForm.meeting} onChange={(e) => setEditForm({ ...editForm, meeting: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="label-lodge">{t("lodgeInfo.logoUrl", "Logo URL")}</label>
                <input className="input-lodge" value={editForm.logo_url} onChange={(e) => setEditForm({ ...editForm, logo_url: e.target.value })} />
              </div>
              <div className="md:col-span-2 gold-divider" />
              <div>
                <label className="label-lodge">{t("superadmin.adminUsername")}</label>
                <input
                  className="input-lodge"
                  placeholder={t("common.optional", "opțional")}
                  value={editForm.admin_username}
                  onChange={(e) => setEditForm({ ...editForm, admin_username: e.target.value })}
                  data-testid="edit-admin-username"
                />
              </div>
              <div>
                <label className="label-lodge">{t("superadmin.adminPassword")}</label>
                <input
                  type="text"
                  className="input-lodge"
                  placeholder={t("common.optional", "opțional, min 4")}
                  value={editForm.admin_password}
                  onChange={(e) => setEditForm({ ...editForm, admin_password: e.target.value })}
                  data-testid="edit-admin-password"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" className="btn-outline-gold" onClick={() => setEditFor(null)}>{t("common.cancel")}</button>
              <button type="submit" className="btn-primary-gold" disabled={editSaving} data-testid="edit-lodge-submit">
                {editSaving ? "..." : t("common.save")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Manage access modal */}
      {accessFor && (
        <div className="dialog-overlay" onClick={() => setAccessFor(null)}>
          <form
            className="dialog-panel"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitAccess}
            data-testid="manage-access-form"
            style={{ maxWidth: 520 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title" style={{ margin: 0 }}>
                {t("superadmin.manageAccessTitle")} · {accessFor.name}
              </h2>
              <button type="button" onClick={() => setAccessFor(null)}>
                <X size={20} />
              </button>
            </div>
            <p className="kv-label" style={{ fontSize: "0.66rem", marginBottom: "0.8rem" }}>
              {t("superadmin.accessHelp")}
            </p>
            <AccessFieldset value={accessForm} onChange={(p) => setAccessForm({ ...accessForm, ...p })} idPrefix="manage" />
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" className="btn-outline-gold" onClick={() => setAccessFor(null)}>
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                className="btn-primary-gold"
                disabled={accessSaving}
                data-testid="save-access-btn"
              >
                {accessSaving ? "..." : t("common.save")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Send message modal */}
      {showMessage && (
        <div className="dialog-overlay" onClick={() => setShowMessage(false)}>
          <form
            className="dialog-panel"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitMessage}
            data-testid="send-message-form"
            style={{ maxWidth: 600 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title" style={{ margin: 0 }}>
                {t("superadmin.sendMessageTitle")}
              </h2>
              <button type="button" onClick={() => setShowMessage(false)}>
                <X size={20} />
              </button>
            </div>
            <p className="kv-label" style={{ fontSize: "0.66rem", marginBottom: "0.8rem" }}>
              {t("superadmin.messageHelp")}
            </p>
            <div className="space-y-4">
              <div>
                <label className="label-lodge">
                  {t("superadmin.messageRecipients")} · {messageRecipientLabel}
                </label>
                <label className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    checked={messageForm.all}
                    onChange={(e) => setMessageForm({ ...messageForm, all: e.target.checked, lodge_ids: [] })}
                    data-testid="msg-all-lodges"
                  />
                  <span>{t("superadmin.messageAllLodges")}</span>
                </label>
                {!messageForm.all && (
                  <div
                    style={{
                      maxHeight: 180,
                      overflowY: "auto",
                      border: "1px solid var(--cream-200)",
                      padding: "0.5rem",
                      marginTop: "0.4rem",
                    }}
                  >
                    {lodges.length === 0 ? (
                      <div className="kv-label">{t("recognized.noResults")}</div>
                    ) : (
                      lodges.map((l) => (
                        <label key={l.id} className="flex items-center gap-2 py-1">
                          <input
                            type="checkbox"
                            checked={messageForm.lodge_ids.includes(l.id)}
                            onChange={() => toggleRecipient(l.id)}
                            data-testid={`msg-lodge-${l.id}`}
                          />
                          <span>
                            {l.name}{" "}
                            <span className="kv-label" style={{ marginLeft: 6 }}>
                              {l.city}
                            </span>
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="label-lodge">{t("superadmin.messageTitle")} *</label>
                <input
                  className="input-lodge"
                  required
                  value={messageForm.title}
                  onChange={(e) => setMessageForm({ ...messageForm, title: e.target.value })}
                  data-testid="msg-title"
                />
              </div>
              <div>
                <label className="label-lodge">{t("superadmin.messageBody")}</label>
                <textarea
                  rows={5}
                  className="input-lodge"
                  value={messageForm.body}
                  onChange={(e) => setMessageForm({ ...messageForm, body: e.target.value })}
                  data-testid="msg-body"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" className="btn-outline-gold" onClick={() => setShowMessage(false)}>
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                className="btn-primary-gold"
                disabled={messageSending || (!messageForm.all && messageForm.lodge_ids.length === 0)}
                data-testid="msg-send-btn"
              >
                {messageSending ? "..." : t("superadmin.sendMessage")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Reset admin modal */}
      {resetFor && (
        <div className="dialog-overlay" onClick={() => setResetFor(null)}>
          <form className="dialog-panel" onClick={(e) => e.stopPropagation()} onSubmit={resetAdmin}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title" style={{ margin: 0 }}>
                {t("superAdmin.reset_admin")} · {resetFor.name}
              </h2>
              <button type="button" onClick={() => setResetFor(null)}>
                <X size={20} />
              </button>
            </div>
            <p style={{ color: "#34322d", marginBottom: "1rem" }}>
              {t("superAdmin.if_the_username_already_exists")}
            </p>
            <div className="space-y-4">
              <div>
                <label className="label-lodge">{t("login.username")} (email)</label>
                <input
                  type="email"
                  className="input-lodge"
                  required
                  value={resetForm.username}
                  onChange={(e) => setResetForm({ ...resetForm, username: e.target.value })}
                  data-testid="reset-username-input"
                />
              </div>
              <div>
                <label className="label-lodge">{t("users.name")}</label>
                <input
                  className="input-lodge"
                  required
                  value={resetForm.name}
                  onChange={(e) => setResetForm({ ...resetForm, name: e.target.value })}
                  data-testid="reset-name-input"
                />
              </div>
              <div className="kv-label" style={{ fontSize: "0.66rem" }}>
                {t("superAdmin.a_new_strong_temporary_password")}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" className="btn-outline-gold" onClick={() => setResetFor(null)}>
                {t("common.cancel")}
              </button>
              <button type="submit" className="btn-primary-gold" data-testid="reset-save-btn">
                {t("common.save")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Auto-generated password reveal — surfaced once after RESET */}
      {resetGeneratedPwd && (
        <div className="dialog-overlay" onClick={() => { setResetGeneratedPwd(null); setResetFor(null); setResetForm({ username: "", password: "", name: "", role: "admin" }); }}>
          <div className="dialog-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }} data-testid="reset-generated-pwd-modal">
            <div className="flex items-center justify-between mb-3">
              <h2 className="dialog-title" style={{ margin: 0 }}>
                {t("superAdmin.temporary_password")}
              </h2>
            </div>
            <p className="kv-label" style={{ marginBottom: 10 }}>
              {t("superAdmin.copy_and_share_this_once")}
            </p>
            <div style={{ background: "#fdf4e0", border: "1px solid #c9a84c", padding: 12, fontFamily: "monospace", fontSize: "1rem", letterSpacing: "0.04em", textAlign: "center", borderRadius: 6 }} data-testid="reset-pwd-value">
              {resetGeneratedPwd.password}
            </div>
            <div className="kv-label" style={{ marginTop: 6, fontSize: "0.7rem" }}>
              <strong>{t("superAdmin.username")}:</strong> {resetGeneratedPwd.username}
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button
                type="button"
                className="btn-outline-gold"
                onClick={() => navigator.clipboard.writeText(resetGeneratedPwd.password).then(() => toast.success(t("superAdmin.copied_2")))}
                data-testid="reset-pwd-copy-btn"
              >
                {t("superAdmin.copy")}
              </button>
              <button
                type="button"
                className="btn-primary-gold"
                onClick={() => { setResetGeneratedPwd(null); setResetFor(null); setResetForm({ username: "", password: "", name: "", role: "admin" }); }}
                data-testid="reset-pwd-close-btn"
              >
                {t("common.close", "Închide")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MFA picker — list every account in a lodge so the SA can reset
          MFA for ONE specific person at a time. */}
      {mfaPickerFor && (
        <div className="dialog-overlay" onClick={() => setMfaPickerFor(null)}>
          <div className="dialog-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 540 }} data-testid="mfa-picker-modal">
            <div className="flex items-center justify-between mb-3">
              <h2 className="dialog-title" style={{ margin: 0 }}>
                {t("superAdmin.reset_mfa")} · {mfaPickerFor.name}
              </h2>
              <button type="button" onClick={() => setMfaPickerFor(null)}><X size={20} /></button>
            </div>
            <p className="kv-label" style={{ fontSize: "0.7rem", marginBottom: 10 }}>
              {t("superAdmin.pick_a_single_account_to")}
            </p>
            {mfaPickerLoading ? (
              <div className="kv-label">…</div>
            ) : mfaPickerUsers.length === 0 ? (
              <div className="kv-label" style={{ fontStyle: "italic" }}>
                {t("superAdmin.no_accounts_yet_for_this")}
              </div>
            ) : (
              <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
                {mfaPickerUsers.map((u) => (
                  <div
                    key={u.id}
                    className="flex items-center justify-between gap-3 py-2"
                    style={{ borderTop: "1px solid var(--cream-200)" }}
                    data-testid={`mfa-user-row-${u.id}`}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "Crimson Text, serif", fontSize: "0.95rem", color: "#0d1b2a" }}>
                        <strong>{u.name || u.username}</strong>{" "}
                        <span className="kv-label" style={{ marginLeft: 6 }}>{u.username}</span>
                      </div>
                      <div className="kv-label" style={{ fontSize: "0.65rem" }}>
                        {u.role}{u.mfa_enabled ? t("superAdmin.mfa_active") : t("superAdmin.mfa_off")}
                      </div>
                    </div>
                    <button
                      className="btn-outline-gold flex items-center gap-1"
                      onClick={() => resetSingleUserMfa(u)}
                      disabled={!u.mfa_enabled}
                      title={u.mfa_enabled ? "" : t("superAdmin.mfa_is_already_off")}
                      data-testid={`mfa-reset-btn-${u.id}`}
                    >
                      <Smartphone size={11} /> {t("superAdmin.reset")}
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-3 mt-5">
              <button type="button" className="btn-outline-gold" onClick={() => setMfaPickerFor(null)}>
                {t("common.close", "Închide")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change password modal */}
      {showPwd && (
        <div className="dialog-overlay" onClick={() => setShowPwd(false)}>
          <form
            className="dialog-panel"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitPwdChange}
            style={{ maxWidth: 460 }}
            data-testid="change-password-form"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title" style={{ margin: 0 }}>
                {t("superadmin.changePasswordTitle")}
              </h2>
              <button type="button" onClick={() => setShowPwd(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="label-lodge">{t("superadmin.currentPassword")} *</label>
                <input
                  type="password"
                  className="input-lodge"
                  required
                  value={pwdForm.current_password}
                  onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })}
                  data-testid="pwd-current"
                  autoComplete="current-password"
                />
              </div>
              <div>
                <label className="label-lodge">{t("superadmin.newPassword")} *</label>
                <input
                  type="password"
                  className="input-lodge"
                  required
                  minLength={4}
                  value={pwdForm.new_password}
                  onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })}
                  data-testid="pwd-new"
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label className="label-lodge">{t("superadmin.confirmNewPassword")} *</label>
                <input
                  type="password"
                  className="input-lodge"
                  required
                  minLength={4}
                  value={pwdForm.confirm_password}
                  onChange={(e) => setPwdForm({ ...pwdForm, confirm_password: e.target.value })}
                  data-testid="pwd-confirm"
                  autoComplete="new-password"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" className="btn-outline-gold" onClick={() => setShowPwd(false)}>
                {t("common.cancel")}
              </button>
              <button type="submit" className="btn-primary-gold" disabled={pwdSaving} data-testid="pwd-submit">
                {pwdSaving ? "..." : t("common.save")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

/**
 * Reusable fieldset for picking the access window. Used both at lodge creation
 * and during access management. Controlled component.
 */
function AccessFieldset({ value, onChange, idPrefix }) {
  const { t } = useTranslation();
  const setMode = (m) => onChange({ accessMode: m });
  return (
    <div className="space-y-2" data-testid={`${idPrefix}-access-fieldset`}>
      <label className="flex items-center gap-2">
        <input
          type="radio"
          name={`${idPrefix}-access`}
          checked={value.accessMode === "trial7"}
          onChange={() => setMode("trial7")}
          data-testid={`${idPrefix}-mode-trial7`}
        />
        <span>{t("superadmin.accessOptionTrial7")}</span>
      </label>
      <label className="flex items-center gap-2">
        <input
          type="radio"
          name={`${idPrefix}-access`}
          checked={value.accessMode === "trialN"}
          onChange={() => setMode("trialN")}
          data-testid={`${idPrefix}-mode-trialN`}
        />
        <span>{t("superadmin.accessOptionTrialN")}</span>
        {value.accessMode === "trialN" && (
          <input
            type="number"
            min={1}
            max={365}
            className="input-lodge"
            style={{ width: 100, padding: "0.3rem 0.5rem", marginLeft: "0.4rem" }}
            value={value.trialDays}
            onChange={(e) => onChange({ trialDays: e.target.value })}
            data-testid={`${idPrefix}-trial-days-input`}
          />
        )}
      </label>
      <label className="flex items-center gap-2">
        <input
          type="radio"
          name={`${idPrefix}-access`}
          checked={value.accessMode === "custom"}
          onChange={() => setMode("custom")}
          data-testid={`${idPrefix}-mode-custom`}
        />
        <span>{t("superadmin.accessOptionCustom")}</span>
        {value.accessMode === "custom" && (
          <input
            type="datetime-local"
            className="input-lodge"
            style={{ width: 220, padding: "0.3rem 0.5rem", marginLeft: "0.4rem" }}
            value={value.customDateTime}
            onChange={(e) => onChange({ customDateTime: e.target.value })}
            required
            data-testid={`${idPrefix}-custom-datetime-input`}
          />
        )}
      </label>
      <label className="flex items-center gap-2">
        <input
          type="radio"
          name={`${idPrefix}-access`}
          checked={value.accessMode === "unlimited"}
          onChange={() => setMode("unlimited")}
          data-testid={`${idPrefix}-mode-unlimited`}
        />
        <span>{t("superadmin.accessOptionUnlimited")}</span>
      </label>
    </div>
  );
}
